import * as React from "react";
import { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../contexts/user-context";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { ToastContext } from "../../contexts/toast-context";
import { createOrUpdateBillingInfo, getBillingInfo } from "../../api/billing-info-api.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { throttle } from 'lodash';
import { validateVat } from "../../api/vat-api.js";
import CountrySelect from "../ui/country-select.jsx";

const validateVatDebounced = throttle((countryCode, vat, callback) => {
  validateVat(countryCode, vat)
    .then((response) => callback(response.data))
    .catch(() => callback(true));
}, 1000);

function validateVatDebouncedAsync(countryCode, vat) {
  return new Promise((resolve) => {
    validateVatDebounced(countryCode, vat, resolve);
  });
}

export function BillingInfo({ userId, sx, allowSaveWithoutChange, onLoad, onSaved }) {
  const { t } = useTranslation(["common", "validation", "user"]);
  const { user } = useContext(UserContext);
  const { showSuccess } = useContext(ToastContext);
  const actualUserId = userId || user?.id;

  const schema = yup.object({
    name: yup
      .string()
      .max(255, t("maxLengthValidation", { field: t("name"), max: 255 }))
      .required(t("requiredValidation", { field: t("name") })),
    countryCode: yup
      .string()
      .nullable()
      .required(t("requiredValidation", { field: t("country") })),
    zipCode: yup
      .string()
      .max(255, t("maxLengthValidation", { field: t("zipCode"), max: 255 }))
      .required(t("requiredValidation", { field: t("zipCode") })),
    city: yup
      .string()
      .max(255, t("maxLengthValidation", { field: t("city"), max: 255 }))
      .required(t("requiredValidation", { field: t("city") })),
    address: yup
      .string()
      .max(255, t("maxLengthValidation", { field: t("address"), max: 255 }))
      .required(t("requiredValidation", { field: t("address") })),
    taxCode: yup
      .string()
      .max(255, t("maxLengthValidation", { field: t("taxCode"), max: 255 }))
      .when(["isCompany"], (isCompany, schema) => {
        if (isCompany[0])
          return schema.required(t("requiredValidation", { field: t("taxCode") }))
        return schema;
      })
      .when(["countryCode"], (countryCode, schema) => {
        if (countryCode[0] != null)
          return schema.test('isValidVat',
            t("invalidVat"),
            async (value) => {
              if (!value)
                return true
              try {
                let res = await validateVatDebouncedAsync(countryCode[0], value);
                return res
              } catch (error) {
                return false
              }
            })
        return schema;
      }),
    isCompany: yup
      .boolean()
  });

  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitted },
    control,
    watch,
    trigger,
    setError,
    reset
  } = useForm({
    defaultValues: {
      name: "",
      countryCode: "HU",
      zipCode: "",
      city: "",
      address: "",
      taxCode: "",
      isCompany: false
    },
    resolver: yupResolver(schema),
  });

  const isCompany = watch("isCompany");
  const countryCode = watch("countryCode");

  useEffect(() => {
    if (isSubmitted) {
      trigger()
        .catch(console.error);
    }
  }, [isCompany, countryCode, trigger, isSubmitted]);

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['billing-info', actualUserId],
    queryFn: () => {
      return getBillingInfo(actualUserId)
    },
    retry: (failureCount, error) => {
      return error.status !== 404 && failureCount < 3;
    },
    enabled: !!actualUserId
  });
  useEffect(() => {
    if (query.data?.data) {
      // Replace null values with empty strings
      const transformedData = Object.fromEntries(
        Object.entries(query.data?.data).map(([key, value]) => [key, value === null ? "" : value])
      )
      reset(transformedData);
      onLoad?.(transformedData);
    }
  }, [query.data]);
  const mutation = useMutation({
    mutationFn: (data) => {
      return createOrUpdateBillingInfo(actualUserId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-info', actualUserId] });
      showSuccess(t("successfulAction"));
      onSaved?.();
    },
    onError: (error) => {
      setError("apiError", {
        message: error?.response?.data?.error_message ||
          t("genericError", { action: t("updateBillingInfo") })
      });
    }
  });

  const onSubmit = (data) => {
    const apiData = { ...data };
    if (apiData.taxCode === "") {
      apiData.taxCode = null;
    }
    mutation.mutate(apiData);
  };

  return (
    <Card
      sx={{
        px: 5,
        py: 3,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        ...sx
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "26px",
            mt: "13px"
          }}
        >
          {t("billingInfo")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 4,
            gap: 3
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormGroup>
            <FormControlLabel control={<Controller
              render={
                ({ field }) =>
                  <Switch
                    {...field}
                    checked={!!field.value}
                  >
                  </Switch>
              }
              control={control}
              name="isCompany"
            />} label={t("billingForCompany")}/>
          </FormGroup>
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                label={t("name")}
                autoComplete="new-password"
                {...field}
              />
            )}
            control={control}
            name="name"
          />
          <Controller
            render={
              ({ field }) => {
                return <CountrySelect
                  textFieldProps={{
                    error: !!errors.countryCode,
                    label: t("country"),
                    fullWidth: true
                  }} field={field}/>;
              }
            }
            control={control}
            name="countryCode"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.zipCode}
                helperText={errors.zipCode?.message}
                label={t("zipCode")}
                {...field}
              />
            )}
            control={control}
            name="zipCode"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.city}
                helperText={errors.city?.message}
                label={t("city")}
                {...field}
              />
            )}
            control={control}
            name="city"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.message}
                label={t("address")}
                {...field}
              />
            )}
            control={control}
            name="address"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.taxCode}
                helperText={errors.taxCode?.message}
                label={t("taxCode")}
                {...field}
              />
            )}
            control={control}
            name="taxCode"
          />
          {errors.apiError && (
            <FormHelperText error>{errors.apiError.message}</FormHelperText>
          )}
          <Button
            type="submit"
            loading={query.isPending || mutation.isPending}
            disabled={(!isDirty && !allowSaveWithoutChange) && !!query.data}
            variant="contained"
          >
            {t("save")}
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
